import React, { useEffect, useRef, useState } from "react";

import { useToast } from "../context/ToastContext";
import "./ToolPage.css";
import {
  downloadVendorMapReport,
  enqueueVendorMapReport,
  listReportVendors,
} from "../services/mapPolicyApi";
import { extractFilenameFromHeader } from "../utils/fileUtils";
import { extractErrorMessage } from "../utils/errorUtils";
import ToolInfoModal from "../components/common/ToolInfoModal";
import ToolFileUploadField from "./shared/ToolFileUploadField";
import ToolPageLayout from "./shared/ToolPageLayout";
import ToolProgressPanel from "./shared/ToolProgressPanel";
import ToolJobsSection from "./shared/ToolJobsSection";
import useJobsPagination from "./shared/useJobsPagination";


const STATUS_DONE = "done";
const STATUS_FAILED = "failed";


function VendorMapReportTool() {
  // Descriptors describe each vendor's required file slots (key + label +
  // helper). Rendered dynamically — Pikolinos shows two pickers; TAOS/NB show one.
  const [descriptors, setDescriptors] = useState([]);
  const [vendor, setVendor] = useState("");
  const [shopifyFile, setShopifyFile] = useState(null);
  // mapFiles is keyed by the descriptor file key (e.g. "map_file", "map_file_2").
  const [mapFiles, setMapFiles] = useState({});
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [downloadingJobId, setDownloadingJobId] = useState(null);
  const { addToast } = useToast();
  const [showInfoModal, setShowInfoModal] = useState(false);

  const {
    jobs,
    page,
    totalPages,
    totalFinished,
    setPage,
    refetch: refetchJobs,
  } = useJobsPagination({
    endpoint: "/map-policy/reports/jobs",
    cacheKey: "vendorMapReport.jobs",
    pageSize: 10,
  });

  const activeDescriptor = descriptors.find((d) => d.slug === vendor);

  const toolInfo = {
    toolName: "Vendor MAP Report",
    description:
      "Compare current Shopify pricing against a vendor's MAP list and get a categorized report: Off MAP, On MAP, Price Drops, MAP Violation, Discounted Items, and vendor-specific extras (NB: upcoming MAP/Off MAP; Pikolinos: MAP Protected w/ no price).",
    steps: [
      "Pick the vendor — each one has its own MAP file format and pricing convention.",
      "Upload the Shopify file — the output of the Find Products by SKU tool (must have a 'Results' sheet).",
      "Upload the vendor MAP file(s). Some vendors (Pikolinos) ship two files; the picker lists exactly what's needed.",
      "Click Start. When the job finishes, download the multi-sheet xlsx.",
    ],
    templates: [],
  };

  const guideTips = [
    "Off MAP = vendor has released the style; price freely.",
    "Price Drops = on MAP, our price is above the target — drop to align.",
    "MAP Violation = on MAP, our price is below MAP — raise immediately. NB also suppresses these when MAP Exception covers the price.",
    "Discounted Items = Variant Price < Compare At Price. Independent of MAP state.",
    "TAOS target = MAP - $0.05. NB and Pikolinos targets = MAP itself (vendor MAP$ already ends in .95).",
    "Pikolinos uses two files — Spring 26 MAP (currently on-MAP list) wins on conflict; the Off MAP file fills in the rest.",
  ];

  // Load vendor descriptors once.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await listReportVendors();
        if (cancelled) return;
        const descs = data.descriptors || [];
        setDescriptors(descs);
        if (descs.length > 0) setVendor(descs[0].slug);
      } catch (error) {
        console.error(error);
        addToast("Failed to load supported vendors.", "error");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [addToast]);

  // Reset uploaded MAP files when switching vendors (so the user can't
  // accidentally submit a stale file from a previous vendor's slot).
  useEffect(() => {
    setMapFiles({});
  }, [vendor]);

  const previousStatusRef = useRef({});
  useEffect(() => {
    const previous = previousStatusRef.current;
    jobs.forEach((job) => {
      const prevStatus = previous[job.job_id];
      if (prevStatus && prevStatus !== job.status) {
        if (job.status === STATUS_DONE) {
          addToast(`MAP report ${job.job_id} complete.`, "success");
        } else if (job.status === STATUS_FAILED) {
          addToast(
            `MAP report ${job.job_id} failed: ${job.error || "unknown error"}`,
            "error"
          );
        }
      }
    });
    previousStatusRef.current = Object.fromEntries(
      jobs.map((j) => [j.job_id, j.status])
    );
  }, [jobs, addToast]);

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!vendor) {
      addToast("Please pick a vendor.", "error");
      return;
    }
    if (!shopifyFile) {
      addToast("Please upload the Shopify file (Find Products by SKU output).", "error");
      return;
    }
    const requiredFiles = (activeDescriptor?.files || []).filter((f) => f.required);
    const missing = requiredFiles.filter((f) => !mapFiles[f.key]);
    if (missing.length > 0) {
      addToast(
        `Please upload: ${missing.map((m) => m.label).join(", ")}.`,
        "error"
      );
      return;
    }
    const maxSize = 100 * 1024 * 1024;
    if (shopifyFile.size > maxSize) {
      addToast("Shopify file is too large. Maximum size is 100MB.", "error");
      return;
    }
    for (const f of requiredFiles) {
      const file = mapFiles[f.key];
      if (file && file.size > maxSize) {
        addToast(`${f.label} is too large. Maximum size is 100MB.`, "error");
        return;
      }
    }

    setUploading(true);
    setUploadProgress(0);
    try {
      const data = await enqueueVendorMapReport(
        { vendor, shopifyFile, files: mapFiles },
        {
          onUploadProgress: (progressEvent) => {
            const percent = Math.round(
              (progressEvent.loaded * 100) / (progressEvent.total || 1)
            );
            setUploadProgress(percent);
          },
        }
      );

      const jobId = data?.job_id;
      if (!jobId) {
        addToast("Server did not return a job ID.", "error");
        return;
      }
      addToast(`Job ${jobId} queued. Tracking progress…`, "info");
      if (page !== 1) await setPage(1);
      else await refetchJobs();
      setShopifyFile(null);
      setMapFiles({});
    } catch (error) {
      console.error(error);
      const message = await extractErrorMessage(error, "Failed to start job.");
      addToast(message, "error");
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const handleDownload = async (job) => {
    if (!job?.job_id) return;
    setDownloadingJobId(job.job_id);
    try {
      const response = await downloadVendorMapReport(job.job_id);
      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;

      const contentDisposition =
        response.headers["content-disposition"] ||
        response.headers["Content-Disposition"];
      const extractedFilename = extractFilenameFromHeader(contentDisposition);
      link.setAttribute(
        "download",
        extractedFilename || job.result_filename || "vendor_map_report.xlsx"
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      addToast("Report downloaded.", "success");
    } catch (error) {
      console.error(error);
      const message = await extractErrorMessage(error, "Failed to download report.");
      addToast(message, "error");
    } finally {
      setDownloadingJobId(null);
    }
  };

  return (
    <>
      <ToolPageLayout
        splitLayout
        title="Vendor MAP Report"
        subtitle={toolInfo.description}
        steps={toolInfo.steps}
        templates={toolInfo.templates}
        tips={guideTips}
        onOpenInfo={() => setShowInfoModal(true)}
      >
        <form onSubmit={handleSubmit} className="tool-form">
          <div className="tool-form-main">
            <section className="tool-section">
              <div className="tool-section-header">
                <h3 className="tool-section-title">Vendor</h3>
              </div>
              <select
                className="tool-input"
                value={vendor}
                onChange={(e) => setVendor(e.target.value)}
                disabled={uploading || descriptors.length === 0}
              >
                {descriptors.length === 0 ? (
                  <option value="">Loading…</option>
                ) : (
                  descriptors.map((d) => (
                    <option key={d.slug} value={d.slug}>
                      {d.label}
                    </option>
                  ))
                )}
              </select>
            </section>

            <section className="tool-section">
              <div className="tool-section-header">
                <h3 className="tool-section-title">Input Files</h3>
              </div>
              <ToolFileUploadField
                label="Shopify file (Find Products by SKU output)"
                file={shopifyFile}
                setFile={setShopifyFile}
                accept=".xlsx,.xls"
                helperText="XLSX with a 'Results' sheet — the output of the Find Products by SKU tool."
              />
              {(activeDescriptor?.files || []).map((fileDesc) => (
                <ToolFileUploadField
                  key={fileDesc.key}
                  label={fileDesc.label}
                  file={mapFiles[fileDesc.key] || null}
                  setFile={(f) =>
                    setMapFiles((prev) => ({ ...prev, [fileDesc.key]: f }))
                  }
                  accept=".xlsx,.xls"
                  helperText={fileDesc.helper}
                />
              ))}
            </section>

            <div className="tool-action-stack">
              <button type="submit" className="tool-btn-primary" disabled={uploading}>
                {uploading ? "Uploading…" : "Start"}
              </button>

              <ToolProgressPanel
                loading={uploading}
                uploadProgress={uploadProgress}
                uploadMessage="Uploading files…"
                processingMessage="Queueing job…"
              />
            </div>
          </div>
        </form>

        <ToolJobsSection
          title="Your Jobs"
          emptyText="No jobs yet. Submit files above to start one."
          jobs={jobs}
          page={page}
          totalPages={totalPages}
          totalFinished={totalFinished}
          onPageChange={setPage}
          renderJob={(job) => {
                const progress = job.progress || {};
                return (
                  <div key={job.job_id} className="tool-job-status">
                    <div className="tool-job-status-row">
                      <span className="tool-job-label">Job ID</span>
                      <span className="tool-job-value">{job.job_id}</span>
                    </div>
                    <div className="tool-job-status-row">
                      <span className="tool-job-label">Vendor</span>
                      <span className="tool-job-value">{(job.vendor || "").toUpperCase()}</span>
                    </div>
                    <div className="tool-job-status-row">
                      <span className="tool-job-label">Input</span>
                      <span className="tool-job-value">{job.input_filename || "—"}</span>
                    </div>
                    <div className="tool-job-status-row">
                      <span className="tool-job-label">Status</span>
                      <span className={`tool-job-value tool-job-status-${job.status}`}>
                        {job.status}
                        {progress.stage ? ` (${progress.stage})` : ""}
                      </span>
                    </div>
                    {job.status === STATUS_DONE && progress.matched_variants !== undefined ? (
                      <div className="tool-job-counts">
                        <span>Off MAP: {progress.off_map_parents || 0} ({progress.off_map_variants || 0} variants)</span>
                        <span>On MAP: {progress.on_map_parents || 0} ({progress.on_map_variants || 0})</span>
                        <span>Price Drops: {progress.price_drops_parents || 0} ({progress.price_drops_variants || 0})</span>
                        <span>MAP Violation: {progress.map_violation_parents || 0} ({progress.map_violation_variants || 0})</span>
                        <span>Discounted: {progress.discounted_parents || 0} ({progress.discounted_variants || 0})</span>
                        {progress.upcoming_map_parents !== undefined ? (
                          <span>Upcoming MAP: {progress.upcoming_map_parents} ({progress.upcoming_map_variants || 0})</span>
                        ) : null}
                        {progress.upcoming_off_map_parents !== undefined ? (
                          <span>Upcoming Off MAP: {progress.upcoming_off_map_parents} ({progress.upcoming_off_map_variants || 0})</span>
                        ) : null}
                        {progress.map_protected_no_price_parents !== undefined ? (
                          <span>MAP Protected (no price): {progress.map_protected_no_price_parents} ({progress.map_protected_no_price_variants || 0})</span>
                        ) : null}
                      </div>
                    ) : null}
                    {job.status === STATUS_FAILED && job.error ? (
                      <div className="tool-job-error">Error: {job.error}</div>
                    ) : null}
                    {job.status === STATUS_DONE ? (
                      <button
                        type="button"
                        className="tool-btn-primary"
                        onClick={() => handleDownload(job)}
                        disabled={downloadingJobId === job.job_id}
                      >
                        {downloadingJobId === job.job_id ? "Downloading…" : "Download Report"}
                      </button>
                    ) : null}
                  </div>
                );
              }}
            />
      </ToolPageLayout>

      <ToolInfoModal
        isOpen={showInfoModal}
        onClose={() => setShowInfoModal(false)}
        toolName={toolInfo.toolName}
        description={toolInfo.description}
        steps={toolInfo.steps}
        templates={toolInfo.templates}
      />
    </>
  );
}


export default VendorMapReportTool;
